/**
 * What a doer may write to their own profile.
 *
 * `PATCH /v1/doer-profiles/me` is the only writer, and this is its whole
 * contract: an omitted key means "leave this alone", an explicit `null` means
 * "clear it".
 */
import { z } from 'zod';
import { DOER_PROFILE_BIO_MAX, DOER_PROFILE_SKILLS_MAX, DOER_PROFILE_SKILL_MAX_LENGTH, } from '../types/doer-profile.js';
import { videoLinkSchema } from './video-links.js';
import { webUrlSchema } from './web-urls.js';
/** One skill or tool — a chip, not a sentence. */
export const doerProfileSkillSchema = z
    .string()
    .trim()
    .min(1, 'A skill cannot be blank')
    .max(DOER_PROFILE_SKILL_MAX_LENGTH, `Keep each skill under ${DOER_PROFILE_SKILL_MAX_LENGTH} characters`);
export const doerProfileSkillsSchema = z
    .array(doerProfileSkillSchema)
    .max(DOER_PROFILE_SKILLS_MAX, `Pick your top ${DOER_PROFILE_SKILLS_MAX} — a longer list reads as none`)
    // Case-insensitively unique, and enforced HERE rather than only in the chip
    // editor: both renderers key React elements on the skill and remove by value,
    // so a duplicate that arrived through any other path would collide on the key
    // and make one remove delete both chips.
    .refine((skills) => new Set(skills.map((skill) => skill.toLowerCase())).size === skills.length, { message: 'That one is already on the list' });
/**
 * The bio, as both the API and the editor's own form enforce it — the editor
 * imports this rather than restating the bound and its sentence, which is how
 * a copy-editing pass ships a client that disagrees with the server.
 */
export const doerProfileBioSchema = z
    .string()
    .max(DOER_PROFILE_BIO_MAX, `Keep your bio under ${DOER_PROFILE_BIO_MAX} characters`);
/**
 * The video the doer is setting.
 *
 * The client says where the URL came from; the SERVER derives the stored
 * `doer_intro_video_kind` from it — a `link` becomes whichever provider
 * `parseVideoLink` recognised. A client naming the provider itself would be a
 * second copy of that rule, free to drift from the one the server enforces.
 */
export const doerIntroVideoInputSchema = z.discriminatedUnion('source', [
    z.object({ source: z.literal('upload'), url: webUrlSchema }),
    z.object({ source: z.literal('link'), url: videoLinkSchema }),
]);
/**
 * The PATCH body: any non-empty subset of the doer's own fields.
 *
 * `.strict()` because the editor saves one field at a time and a typo'd key
 * would otherwise be silently dropped, leaving the doer looking at a form that
 * says it saved.
 */
export const doerProfilePatchSchema = z
    .object({
    bio: doerProfileBioSchema.nullable(),
    skills: doerProfileSkillsSchema,
    intro_video: doerIntroVideoInputSchema.nullable(),
    booking_url: webUrlSchema.nullable(),
})
    .partial()
    .strict()
    .refine((patch) => Object.keys(patch).length > 0, {
    message: 'Provide at least one of bio, skills, intro_video or booking_url',
});
//# sourceMappingURL=doer-profile.js.map